import {
  ArrowLeftRight,
  BookOpen,
  HelpCircle,
  Maximize2,
  Settings,
  StickyNote,
  Volume2,
  VolumeX,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { TooltipIconButton } from "@/components/trading/TooltipIconButton";
import { Badge } from "@/components/ui/badge";

interface TradingToolbarProps {
  soundEnabled: boolean;
  mutedUntilTs: number;
  now: number;
  onSwapPanes: () => void;
  onToggleSound: () => void;
  onOpenBriefings: () => void;
  onOpenNotes?: () => void;
  onOpenSettings: () => void;
  onOpenKeyboardHelp: () => void;
  onToggleFullscreen: () => void;
}

/**
 * Trading-shell action toolbar (swap panes, sound, briefings, settings,
 * keyboard help, fullscreen). Renamed from `Topbar` so it doesn't collide
 * case-insensitively with the global `shell/TopBar.tsx` on macOS APFS.
 */
export function TradingToolbar({
  soundEnabled,
  mutedUntilTs,
  now,
  onSwapPanes,
  onToggleSound,
  onOpenBriefings,
  onOpenNotes,
  onOpenSettings,
  onOpenKeyboardHelp,
  onToggleFullscreen,
}: TradingToolbarProps) {
  const { t } = useTranslation("dashboard");
  const SoundIcon = soundEnabled ? Volume2 : VolumeX;
  const mutedSeconds = Math.max(0, Math.ceil((mutedUntilTs - now) / 1_000));
  const mutedLabel =
    mutedSeconds > 0
      ? t("toolbar.muted", { minutes: Math.ceil(mutedSeconds / 60) })
      : null;

  return (
    <header className="flex items-center justify-between gap-4 border-b border-border px-4 py-3 mb-3">
      <h1 className="text-base font-bold m-0">{t("toolbar.title")}</h1>
      <nav aria-label={t("toolbar.actionsAria")} className="flex items-center gap-1.5">
        <TooltipIconButton label={t("toolbar.swap")} onClick={onSwapPanes}>
          <ArrowLeftRight className="h-4 w-4" />
        </TooltipIconButton>
        <TooltipIconButton
          ariaPressed={soundEnabled}
          label={soundEnabled ? t("toolbar.soundDisable") : t("toolbar.soundEnable")}
          onClick={onToggleSound}
        >
          <SoundIcon className="h-4 w-4" />
        </TooltipIconButton>
        <TooltipIconButton label={t("toolbar.openBriefings")} onClick={onOpenBriefings}>
          <BookOpen className="h-4 w-4" />
        </TooltipIconButton>
        {onOpenNotes ? (
          <TooltipIconButton label={t("toolbar.openNotes")} onClick={onOpenNotes}>
            <StickyNote className="h-4 w-4" />
          </TooltipIconButton>
        ) : null}
        <TooltipIconButton label={t("toolbar.openSettings")} onClick={onOpenSettings}>
          <Settings className="h-4 w-4" />
        </TooltipIconButton>
        <TooltipIconButton label={t("toolbar.keyboardHelp")} onClick={onOpenKeyboardHelp}>
          <HelpCircle className="h-4 w-4" />
        </TooltipIconButton>
        <TooltipIconButton label={t("toolbar.toggleFullscreen")} onClick={onToggleFullscreen}>
          <Maximize2 className="h-4 w-4" />
        </TooltipIconButton>
        {mutedLabel && (
          <Badge variant="secondary" className="ms-2 gap-1">
            <VolumeX className="h-3 w-3" /> {mutedLabel}
          </Badge>
        )}
      </nav>
    </header>
  );
}
